import _ from "lodash";

import { AccountMetadata, LocalStorageProject } from "../reducers/environments";
import {
  DestinationConnectionMode,
  LoadedDestinationInfo,
} from "../types/global";
import { isAccountInHIPAAMode } from "../util";
import { areAllRequiredPropertiesAllowed } from "./DestinationPropertyMetadata";
import { SaaSDestination } from "./destinations";

export enum DestinationValidity {
  valid,
  partiallyInvalid,
  totallyInvalid,
  totallyInvalidClientModeWhenHIPAA,
  totallyInvalidMissingAllowlistWhenHIPAA,
}

export interface DestinationValidationResult {
  validity: DestinationValidity;
  warningMessage?: string;
}

function getRequiredConfigKeys(destination: SaaSDestination) {
  return _.chain(destination.configFields)
    .filter(({ required }) => required)
    .map(({ key }) => key)
    .value();
}

// isDestinationFullyConfigured checks whether all of the destination's required
// config fields have been set up
export function isDestinationFullyConfigured(
  configuredDest: LoadedDestinationInfo,
  destination: SaaSDestination
): boolean {
  return (
    configuredDest &&
    _.every(
      getRequiredConfigKeys(destination),
      (key) => key in configuredDest.settings
    )
  );
}

// isDestinationPartiallyConfigured checks whether at least one of the
// destination's required config fields has been set up
export function isDestinationPartiallyConfigured(
  configuredDest: LoadedDestinationInfo,
  destination: SaaSDestination
): boolean {
  return (
    configuredDest &&
    _.some(
      getRequiredConfigKeys(destination),
      (key) => key in configuredDest.settings
    )
  );
}

// Returns true iff 'dest' is valid for an event coming from 'source'.
export function destinationIsValidForSource(
  dest: LoadedDestinationInfo,
  source: DestinationConnectionMode
): boolean {
  if (dest.connection_mode === "server") {
    // If the connection mode is server-side, the destination only needs to be valid server-side.
    return dest.is_valid_server_side;
  }

  if (!dest.is_valid_client_side) {
    // If the destination is specified as client-side, the destination must be valid client-side.
    return false;
  }

  // Either the event must be from the client, or the server-side implementation must be valid.
  return source === "client" || dest.is_valid_server_side;
}

export function destinationIsValid(
  configuredDest: LoadedDestinationInfo,
  destMetadata: SaaSDestination
): boolean {
  const isValidClientSide = destinationIsValidForSource(
    configuredDest,
    "client"
  );
  const isValidServerSide = destinationIsValidForSource(
    configuredDest,
    "server"
  );

  return (
    isDestinationFullyConfigured(configuredDest, destMetadata) &&
    (isValidClientSide || isValidServerSide)
  );
}

export function getDestinationValidity(
  accountMetadata: AccountMetadata,
  project: LocalStorageProject,
  configuredDest: LoadedDestinationInfo,
  destination: SaaSDestination
): DestinationValidationResult {
  if (!isDestinationPartiallyConfigured(configuredDest, destination)) {
    return {
      validity: DestinationValidity.valid,
    };
  }

  if (
    isAccountInHIPAAMode(accountMetadata) &&
    configuredDest.connection_mode === "client" &&
    !configuredDest.has_baa
  ) {
    return {
      validity: DestinationValidity.totallyInvalidClientModeWhenHIPAA,
      warningMessage:
        "This destination's configuration is invalid because the connection mode is 'client' and PHI is being restricted.",
    };
  }

  const isValidClientSide = destinationIsValidForSource(
    configuredDest,
    "client"
  );
  const isValidServerSide = destinationIsValidForSource(
    configuredDest,
    "server"
  );
  if (!isValidClientSide && !isValidServerSide) {
    let invalidWarning: string;
    invalidWarning = "This destination's configuration is invalid.";

    if (!isDestinationFullyConfigured(configuredDest, destination)) {
      invalidWarning += " Required fields are missing.";
    }
    return {
      validity: DestinationValidity.totallyInvalid,
      warningMessage: invalidWarning,
    };
  } else if (!isValidClientSide) {
    return {
      validity: DestinationValidity.partiallyInvalid,
      warningMessage:
        "This destination's configuration is only valid for server-side events.",
    };
  } else if (!isValidServerSide) {
    return {
      validity: DestinationValidity.partiallyInvalid,
      warningMessage:
        "This destination's configuration is only valid for client-side events.",
    };
  }

  if (
    !areAllRequiredPropertiesAllowed(accountMetadata, project, configuredDest)
  ) {
    return {
      validity: DestinationValidity.totallyInvalidMissingAllowlistWhenHIPAA,
      warningMessage:
        "This destination's configuration is invalid because required properties have not been added to the HIPAA allowlist.",
    };
  }

  return {
    validity: DestinationValidity.valid,
  };
}
